import { format } from 'date-fns';
import type { ScheduleTaskForChart } from '../../../services/scheduleService';
import {
  SYNTHETIC_ID_START,
  parseDateOnly,
  barTypeForTask,
  parseSchedulePlanDate,
  plannedStartAndEndSameCalendarDate,
  ensureMinOneDayBarWhenSamePlannedDate,
  type SvarTask,
} from './scheduleGanttMapping';
import { sortTasksByCompletionDependency } from './scheduleTaskDependencyOrder';

/** Group-row name for tasks that belong to no project: "Routine Work" when that is all it holds. */
export function noProjectGroupLabel(tasks: Array<{ type?: string | null }>): string {
  const allRoutine =
    tasks.length > 0 && tasks.every((t) => String(t.type ?? '').trim().toLowerCase() === 'routine');
  return allRoutine ? 'Routine Work' : 'Tasks Without a Project';
}

interface ProjectBucket {
  id: number;
  name: string;
  number: string | null;
  tasks: ScheduleTaskForChart[];
}

function getTaskStart(task: ScheduleTaskForChart, rangeStart: Date, rangeEnd: Date): Date | null {
  if (task.yearRoundSchedule) return rangeStart;
  return (
    parseSchedulePlanDate(task.identifiedDate ?? task.startDate ?? null) ??
    parseSchedulePlanDate(task.plannedCompletionDate ?? null)
  );
}

function getTaskEnd(task: ScheduleTaskForChart, rangeStart: Date, rangeEnd: Date): Date | null {
  if (task.yearRoundSchedule) return rangeEnd;
  const start = getTaskStart(task, rangeStart, rangeEnd);
  const plannedEnd = parseSchedulePlanDate(task.plannedCompletionDate ?? null);
  if (!start && !plannedEnd) return null;
  // Same calendar day is valid — do not fall through to the +7 day default.
  if (start && plannedEnd && plannedEnd.getTime() >= start.getTime()) return plannedEnd;
  if (start) return new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
  return plannedEnd;
}

function clampTaskRangeToWindow(start: Date, end: Date, rangeStart: Date, rangeEnd: Date): { start: Date; end: Date } {
  const windowStartMs = rangeStart.getTime();
  const windowEndMs = rangeEnd.getTime();
  const startMs = start.getTime();
  const endMs = end.getTime();

  let clampedStartMs = Math.max(startMs, windowStartMs);
  let clampedEndMs = Math.min(endMs, windowEndMs);

  // Ensure a visible bar after clamping.
  if (clampedEndMs <= clampedStartMs) {
    clampedEndMs = Math.min(windowEndMs, clampedStartMs + 24 * 60 * 60 * 1000);
  }
  if (clampedEndMs <= clampedStartMs) {
    clampedStartMs = windowStartMs;
    clampedEndMs = Math.min(windowEndMs, windowStartMs + 24 * 60 * 60 * 1000);
  }

  return { start: new Date(clampedStartMs), end: new Date(clampedEndMs) };
}

export function tasksToSvarTasks(
  sourceTasks: ScheduleTaskForChart[],
  rangeStart: Date,
  rangeEnd: Date
): SvarTask[] {
  const taskList = Array.isArray(sourceTasks) ? sourceTasks : [];
  const buckets = new Map<string, ProjectBucket>();
  for (const task of taskList) {
    const projectKey = task.project?.id != null ? String(task.project.id) : 'no-project';
    const existing = buckets.get(projectKey);
    if (existing) {
      existing.tasks.push(task);
      continue;
    }
    buckets.set(projectKey, {
      id: task.project?.id ?? 0,
      name: task.project?.name ?? '',
      number: task.project?.projectNumber ?? null,
      tasks: [task],
    });
  }

  const orderedBuckets = Array.from(buckets.values()).sort((a, b) => {
    const aLabel = `${a.number ?? ''} ${a.name}`.trim();
    const bLabel = `${b.number ?? ''} ${b.name}`.trim();
    return aLabel.localeCompare(bLabel);
  });

  const svarTasks: SvarTask[] = [];
  let nextSyntheticId = SYNTHETIC_ID_START;

  for (const project of orderedBuckets) {
    const projectRowId = nextSyntheticId++;
    const clampedRanges = project.tasks
      .map((task) => {
        const taskStart = getTaskStart(task, rangeStart, rangeEnd);
        const taskEnd = getTaskEnd(task, rangeStart, rangeEnd);
        if (!taskStart || !taskEnd) return null;
        return clampTaskRangeToWindow(taskStart, taskEnd, rangeStart, rangeEnd);
      })
      .filter((r): r is { start: Date; end: Date } => r != null);
    const projectStarts = clampedRanges.map((r) => r.start);
    const projectEnds = clampedRanges.map((r) => r.end);
    const projectStart = projectStarts.length > 0 ? new Date(Math.min(...projectStarts.map((d) => d.getTime()))) : rangeStart;
    const projectEnd = projectEnds.length > 0 ? new Date(Math.max(...projectEnds.map((d) => d.getTime()))) : rangeEnd;
    const projectLabel = project.number
      ? `${project.number}${project.name ? ` – ${project.name}` : ''}`
      : project.name.trim();
    // A group row always has a name (W-M13). Tasks outside any project used to sit under a blank
    // group row; "No Project" was rejected in review, so the group says what it holds.
    const groupLabel = projectLabel || noProjectGroupLabel(project.tasks);

    const projectPlannedStarts = project.tasks
      .map((t) => (t.identifiedDate ? parseDateOnly(t.identifiedDate) : null))
      .filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()));
    const projectPlannedEnds = project.tasks
      .map((t) => (t.plannedCompletionDate ? parseDateOnly(t.plannedCompletionDate) : null))
      .filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()));

    svarTasks.push({
      id: projectRowId,
      text: groupLabel,
      start: projectStart,
      end: projectEnd,
      progress: 0,
      type: 'summary-empty',
      parent: 0,
      open: true,
      status: 'PROJECT',
      actions: null,
      plannedStartDate:
        projectPlannedStarts.length > 0
          ? format(new Date(Math.min(...projectPlannedStarts.map((d) => d.getTime()))), 'yyyy-MM-dd')
          : null,
      plannedCompletionDate:
        projectPlannedEnds.length > 0
          ? format(new Date(Math.max(...projectPlannedEnds.map((d) => d.getTime()))), 'yyyy-MM-dd')
          : null,
    });

    const orderedProjectTasks = sortTasksByCompletionDependency(project.tasks);
    for (const task of orderedProjectTasks) {
      const rawTaskStart = getTaskStart(task, rangeStart, rangeEnd);
      const rawTaskEnd = getTaskEnd(task, rangeStart, rangeEnd);
      if (!rawTaskStart || !rawTaskEnd) continue;
      const { start: taskStart, end: taskEnd } = clampTaskRangeToWindow(
        rawTaskStart,
        rawTaskEnd,
        rangeStart,
        rangeEnd
      );
      const samePlanDay = plannedStartAndEndSameCalendarDate(
        task.identifiedDate ?? task.startDate,
        task.plannedCompletionDate
      );
      const { start: visStart, end: visEnd } = ensureMinOneDayBarWhenSamePlannedDate(
        taskStart,
        taskEnd,
        rangeEnd,
        samePlanDay
      );
      const taskRowId = nextSyntheticId++;
      const displayName = task.name || task.description || `Task #${task.id}`;
      const hasDependencies =
        !!(task.dependencies && Array.isArray(task.dependencies.triggers) && task.dependencies.triggers.length > 0);
      const hasResourceConflict = Array.isArray(task.assignees) && task.assignees.length > 1;
      const taskBarType = barTypeForTask(task);
      const yr = task.yearRoundSchedule ? '⟳ ' : '';

      svarTasks.push({
        id: taskRowId,
        text: hasResourceConflict
          ? `${hasDependencies ? '⛓ ' : ''}${yr}${displayName} [CONFLICT]`
          : `${hasDependencies ? '⛓ ' : ''}${yr}${displayName}`,
        start: visStart,
        end: visEnd,
        progress: 0,
        type: taskBarType,
        parent: projectRowId,
        open: false,
        status: task.status,
        sourceTaskId: task.id,
        taskNumber: task.taskNumber ?? null,
        project: projectLabel,
        plannedStartDate: task.identifiedDate ?? task.startDate ?? null,
        plannedCompletionDate: task.plannedCompletionDate ?? null,
        dependencies: task.dependencies ?? null,
        yearRoundSchedule: task.yearRoundSchedule === true,
        identifiedAccomplishment:
          task.identifiedAccomplishment != null && Number.isFinite(Number(task.identifiedAccomplishment))
            ? Number(task.identifiedAccomplishment)
            : null,
        actions: 'View task',
      });
    }
  }

  return svarTasks;
}
